import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import React, { FC, createContext, useReducer } from 'react';
import { MentionSuggestion } from '.';

type MentionProviderProps = { children: React.ReactNode };
type State = {
  activeSuggestion?: MentionSuggestion;
  editor?: Editor;
};
type Action =
  | { type: 'SET_ACTIVE_SUGGESTION'; payload: MentionSuggestion }
  | { type: 'SET_EDITOR'; payload: Editor };
type Dispatch = (action: Action) => void;
const MentionsStateContext = createContext<State | undefined>(undefined);
const MentionDispatchContext = createContext<Dispatch | undefined>(undefined);

function mentionReducer(state: State, action: Action) {
  switch (action.type) {
    case 'SET_ACTIVE_SUGGESTION':
      if (action.payload) {
        return { ...state, activeSuggestion: action.payload };
      }

      return state;
    case 'SET_EDITOR':
      return {
        ...state,
        editor: action.payload,
      };
    default: {
      throw new Error(`Unhandled action: ${action}`);
    }
  }
}
const MentionProvider: FC<MentionProviderProps> = ({
  children,
}: MentionProviderProps) => {
  const initialState = {};
  const [state, dispatch] = useReducer(mentionReducer, initialState);

  return (
    <MentionsStateContext.Provider value={state}>
      <MentionDispatchContext.Provider value={dispatch}>
        {children}
      </MentionDispatchContext.Provider>
    </MentionsStateContext.Provider>
  );
};

// for state and dispatches
function useMentionsState(): State {
  const context = React.useContext(MentionsStateContext);

  if (context === undefined) {
    throw new Error('useMentionsState must be used within a MentionProvider');
  }

  return context;
}
function useMentionsDispatch(): Dispatch {
  const context = React.useContext(MentionDispatchContext);

  if (context === undefined) {
    throw new Error(
      'useMentionsDispatch must be used within a MentionProvider',
    );
  }

  return context;
}
function useMentions(): [State, Dispatch] {
  return [useMentionsState(), useMentionsDispatch()];
}

const setActiveSuggestion = (suggestion: MentionSuggestion): Action => {
  return { type: 'SET_ACTIVE_SUGGESTION', payload: suggestion };
};

const setEditor = (editor: Editor): Action => {
  return { type: 'SET_EDITOR', payload: editor };
};

export { MentionProvider, useMentions, setActiveSuggestion, setEditor };
